import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { AreaBroadcastRow } from '@/pages/Admin/area-notifications/api';

interface AreaBroadcastsTrendChartProps {
  items: AreaBroadcastRow[];
  loading: boolean;
}

interface TooltipEntry {
  dataKey: string;
  name: string;
  value: number;
  color: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;

  const get = (key: string): number => {
    const found = payload.find((p) => p.dataKey === key);
    return typeof found?.value === 'number' ? found.value : 0;
  };
  const audience = get('Audience');
  const opens = get('Opens');
  const openRate = audience > 0 ? (opens / audience) * 100 : 0;

  return (
    <div className="bg-gray-950/95 border border-gray-700 rounded-xl px-3 py-2.5 shadow-xl backdrop-blur-sm min-w-[180px]">
      <p className="text-xs font-semibold text-gray-200 mb-2">Week of {label}</p>
      <div className="space-y-1.5">
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-2 text-xs">
            <span
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-400">{entry.name}</span>
            <span className="ml-auto font-medium text-gray-100 tabular-nums">
              {Number(entry.value).toLocaleString()}
            </span>
          </div>
        ))}
        {audience > 0 && (
          <div className="flex items-center gap-2 text-xs pt-1.5 mt-1 border-t border-gray-800">
            <span className="text-gray-500">Open rate</span>
            <span className="ml-auto font-medium text-gray-300 tabular-nums">
              {openRate.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Week-start key (UTC, Mon) so the chart buckets line up with the rest of
// the page's notification-metrics week buckets.
function weekStartIso(d: Date): string {
  const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = utc.getUTCDay(); // 0 = Sun, 1 = Mon ...
  const offset = day === 0 ? -6 : 1 - day; // shift back to Mon
  utc.setUTCDate(utc.getUTCDate() + offset);
  return utc.toISOString().slice(0, 10);
}

function formatWeekLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function AreaBroadcastsTrendChart({ items, loading }: AreaBroadcastsTrendChartProps): React.ReactElement {
  const chartData = useMemo(() => {
    const buckets = new Map<string, { Broadcasts: number; Audience: number; Opens: number }>();
    for (const b of items) {
      if (b.status !== 'completed') continue;
      const key = weekStartIso(new Date(b.created_at));
      const cur = buckets.get(key) ?? { Broadcasts: 0, Audience: 0, Opens: 0 };
      cur.Broadcasts += 1;
      cur.Audience += b.audience_count ?? 0;
      cur.Opens += b.opened_count ?? 0;
      buckets.set(key, cur);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([key, v]) => ({ week: formatWeekLabel(key), ...v }));
  }, [items]);

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="h-5 w-48 bg-gray-800 rounded animate-pulse mb-4" />
        <div className="h-64 bg-gray-800/40 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-white mb-4 tracking-tight">Area Broadcasts — Weekly Trend</h3>
      {chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center">
          <p className="text-sm text-gray-500">No completed broadcasts to chart yet.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} barGap={2} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={{ stroke: '#374151' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: 'rgba(139, 92, 246, 0.08)' }}
            />
            <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
            <Bar
              dataKey="Audience"
              fill="#06b6d4"
              radius={[4, 4, 0, 0]}
              activeBar={{ stroke: '#67e8f9', strokeWidth: 2 }}
            />
            <Bar
              dataKey="Opens"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
              activeBar={{ stroke: '#93c5fd', strokeWidth: 2 }}
            />
            <Bar
              dataKey="Broadcasts"
              fill="#8b5cf6"
              radius={[4, 4, 0, 0]}
              activeBar={{ stroke: '#c4b5fd', strokeWidth: 2 }}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default AreaBroadcastsTrendChart;
